import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Ear, Lock, Eye, EyeSlash, CheckCircle, Warning } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActivateAccountPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    if (!token) { setError("Lien d'activation invalide."); setLoading(false); return; }
    axios.get(`${API}/auth/activate-info?token=${token}`)
      .then(res => { setUserInfo(res.data); setLoading(false); })
      .catch(err => { setError(err.response?.data?.detail || "Lien invalide ou expiré."); setLoading(false); });
  }, [token]);

  const handleActivate = async (e) => {
    e.preventDefault();
    if (!acceptPrivacy) { toast.error("Vous devez accepter la politique de confidentialité."); return; }
    if (password.length < 6) { toast.error("Le mot de passe doit contenir au moins 6 caractères."); return; }
    if (password !== confirmPassword) { toast.error("Les mots de passe ne correspondent pas."); return; }
    setActivating(true);
    try {
      await axios.post(`${API}/auth/activate`, { token, password, accept_privacy: true });
      setSuccess(true);
      toast.success("Compte activé avec succès !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'activation.");
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Ear size={32} weight="bold" className="text-zinc-900" />
          <h1 className="font-heading font-bold text-xl tracking-wider text-zinc-900">HAMAC SOLUTIONS</h1>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-6 sm:p-8">
          {loading && <p className="text-center text-zinc-500">Vérification du lien...</p>}

          {error && (
            <div className="text-center space-y-4">
              <Warning size={48} className="text-red-500 mx-auto" />
              <p className="text-red-600 font-medium">{error}</p>
              <Link to="/login" className="text-sm text-zinc-600 underline">Retour à la connexion</Link>
            </div>
          )}

          {success && (
            <div className="text-center space-y-4">
              <CheckCircle size={48} className="text-emerald-600 mx-auto" />
              <h2 className="font-heading font-semibold text-lg">Compte activé !</h2>
              <p className="text-sm text-zinc-600">Vous pouvez maintenant vous connecter avec votre mot de passe.</p>
              <Link to="/login">
                <Button className="w-full mt-4 bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="go-to-login-btn">
                  Se connecter
                </Button>
              </Link>
            </div>
          )}

          {!loading && !error && !success && userInfo && (
            <form onSubmit={handleActivate} className="space-y-5">
              <div className="text-center mb-4">
                <h2 className="font-heading font-semibold text-lg">Activation de votre compte</h2>
                <p className="text-sm text-zinc-500 mt-1">Bienvenue {userInfo.user_name}</p>
                <p className="text-xs text-zinc-400">{userInfo.user_email}</p>
              </div>

              <div className="space-y-2">
                <Label className="label-overline">Nouveau mot de passe *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                  <Input type={showPass ? 'text' : 'password'} placeholder="Minimum 6 caractères" value={password} onChange={e => setPassword(e.target.value)} className="pl-10 pr-10 rounded-sm" required minLength={6} data-testid="activate-password" />
                  <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
                    {showPass ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="label-overline">Confirmer le mot de passe *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                  <Input type={showPass ? 'text' : 'password'} placeholder="Retapez le mot de passe" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="pl-10 rounded-sm" required data-testid="activate-confirm-password" />
                </div>
              </div>

              <div className="border border-zinc-200 rounded-sm p-4 bg-zinc-50">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="accept-privacy-label">
                  <Checkbox checked={acceptPrivacy} onCheckedChange={setAcceptPrivacy} className="mt-0.5" data-testid="accept-privacy-checkbox" />
                  <span className="text-sm text-zinc-700">
                    J'accepte la{' '}
                    <Link to="/confidentialite" target="_blank" className="text-zinc-900 underline font-semibold" data-testid="privacy-link">
                      politique de confidentialité
                    </Link>
                  </span>
                </label>
              </div>

              <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800 text-white" disabled={activating || !acceptPrivacy} data-testid="activate-submit-btn">
                {activating ? 'Activation...' : 'Activer mon compte'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
